import InfoHelp from "../../components/help/InfoHelp";
import { metricHelp } from "../../components/help/helpText";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../../api/client";
import { saveBlobResponse } from "../../utils/download";
import Header from "../../components/layout/Header";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "../../components/ui/dropdown-menu";
import {
  AlertTriangle, ArrowRight, BarChart3, CalendarClock, CheckCircle2, CircleDollarSign, Clock3,
  Copy, Download, Eye, FileCheck, FileText, GitBranch, History, Link2, Mail, MessageCircle,
  MessageSquareText, MoreHorizontal, Pencil, Plus, RefreshCw, Search, Send, Trash2,
  TrendingUp, XCircle,
} from "lucide-react";
import { formatBRL, formatPct, formatDateBR } from "../../utils/format";
import EmptyState from "../../components/common/EmptyState";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const STATUS_LABEL = {
  rascunho: { label: "Rascunho", cls: "bg-muted text-muted-foreground" },
  enviada: { label: "Em negociação", cls: "bg-primary/10 text-primary" },
  aceita: { label: "Aceita", cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
  recusada: { label: "Perdida", cls: "bg-destructive/10 text-destructive" },
  substituida: { label: "Substituída", cls: "bg-slate-500/10 text-slate-600 dark:text-slate-300" },
};

const STATUS_FILTERS = [
  { key: "todas", label: "Todas" },
  { key: "rascunho", label: "Rascunhos" },
  { key: "enviada", label: "Em negociação" },
  { key: "aceita", label: "Aceitas" },
  { key: "recusada", label: "Perdidas" },
  { key: "substituida", label: "Substituídas" },
];

const dtBR = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const localInputValue = (date = new Date(Date.now() + 24 * 3600 * 1000)) => {
  const d = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 16);
};

const displayProposalCode = (p) => {
  if (!p) return "—";
  if (p.codigo_base && p.revision_number != null) {
    return `${p.codigo_base}-R${String(Math.max(0, Number(p.revision_number) || 0)).padStart(2, "0")}`;
  }
  return p.codigo || (p.numero != null ? String(p.numero) : "—");
};

function Kpi({ icon: Icon, label, value, sub, alert = false }) {
  return (
    <div className={`min-w-0 overflow-hidden rounded-xl border bg-card p-4 ${alert ? "border-amber-500/40 bg-amber-500/[0.025]" : "border-border"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">{label}<InfoHelp label={label}>{metricHelp[label] || sub || "Indicador do conjunto de propostas considerado nesta tela."}</InfoHelp></span>
        <Icon className={`size-4 shrink-0 ${alert ? "text-amber-500" : "text-primary"}`} />
      </div>
      <div className="mt-2 min-w-0 break-words font-mono text-[clamp(1.2rem,2.2vw,1.7rem)] font-semibold leading-tight tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Timeline({ events }) {
  if (!events?.length) return <p className="text-sm text-muted-foreground py-6 text-center">Nenhum evento registrado.</p>;
  return (
    <div className="relative pl-6 space-y-5 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-px before:bg-border">
      {events.map((event, i) => (
        <div key={`${event.kind}-${event.at}-${i}`} className="relative">
          <span className={`absolute -left-6 top-1.5 size-3.5 rounded-full border-2 border-background ${event.kind === "revision_requested" || event.kind === "superseded" ? "bg-amber-500" : "bg-primary"}`} />
          <div className="text-sm font-medium">{event.title}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{dtBR(event.at)}{event.actor && event.actor !== "Sistema" ? ` · ${event.actor}` : ""}</div>
          {event.detail && <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{event.detail}</div>}
        </div>
      ))}
    </div>
  );
}

function RevisionChain({ items = [] }) {
  if (!items.length) return <p className="py-8 text-center text-sm text-muted-foreground">Nenhuma versão encontrada.</p>;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-muted/20 p-4">
        {items.map((item, idx) => (
          <React.Fragment key={item.id}>
            {idx > 0 && <span className="text-muted-foreground">→</span>}
            <div className={`rounded-lg border px-3 py-2 ${item.superseded ? "border-border bg-muted/30" : "border-primary/40 bg-primary/5"}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs font-semibold text-primary">{item.revision_label}</span>
                {!item.superseded && <span className="rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-emerald-600 dark:text-emerald-400">vigente</span>}
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">{item.display_code}</div>
            </div>
          </React.Fragment>
        ))}
      </div>
      <div className="divide-y divide-border rounded-xl border border-border">
        {items.map((item) => {
          const st = item.superseded ? STATUS_LABEL.substituida : (STATUS_LABEL[item.status] || STATUS_LABEL.rascunho);
          return (
            <div key={item.id} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-semibold">{item.display_code}</span>
                  <span className={`rounded px-2 py-0.5 text-[10px] font-mono uppercase ${st.cls}`}>{st.label}</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">Criada em {dtBR(item.created_at)}{item.revision_reason ? ` · ${item.revision_reason}` : ""}</div>
              </div>
              <Link to={`/propostas/${item.id}`}><Button variant="outline" size="sm">{item.superseded || item.status === "aceita" ? "Visualizar" : "Abrir"}</Button></Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ProposalsList() {
  const { isAdmin, can } = useAuth();
  const canChangeStatus = isAdmin || can("can_change_proposal_status");
  const [items, setItems] = useState([]);
  const [crm, setCrm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [statusFilter, setStatusFilter] = useState("todas");
  const [sharingId, setSharingId] = useState(null);
  const [shareInfo, setShareInfo] = useState(null);
  const [timeline, setTimeline] = useState({ open: false, loading: false, proposal: null, events: [] });
  const [revisionChain, setRevisionChain] = useState({ open: false, loading: false, proposal: null, items: [] });
  const [followUp, setFollowUp] = useState({ open: false, proposal: null, scheduled_at: localInputValue(), channel: "telefone", note: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [proposalsRes, metricsRes] = await Promise.all([
        api.get("/proposals"),
        api.get("/proposals/crm/metrics"),
      ]);
      setItems(proposalsRes.data || []);
      setCrm(metricsRes.data || null);
    } catch {
      toast.error("Falha ao carregar propostas");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((p) => {
      const superseded = Boolean(p.superseded_by_id);
      if (statusFilter === "substituida" && !superseded) return false;
      if (statusFilter !== "todas" && statusFilter !== "substituida") {
        if (superseded || p.status !== statusFilter) return false;
      }
      if (!term) return true;
      const hay = `${displayProposalCode(p)} ${p.codigo_base || ""} ${p.numero || ""} ${(p.client_snapshot || {}).razao_social || ""} ${p.revision_request_message || ""} ${p.superseded_by_code || ""}`.toLowerCase();
      return hay.includes(term);
    });
  }, [items, q, statusFilter]);

  const _safeName = (s) => (s || "cliente").replace(/[\\/:*?"<>|]/g, "").trim() || "cliente";
  const _fname = (id, tipo) => {
    const p = items.find((x) => x.id === id);
    const codigo = displayProposalCode(p);
    const cli = _safeName(p?.client_snapshot?.razao_social);
    return codigo && codigo !== "—" ? `${tipo} ${codigo} - ${cli}` : `${tipo} - ${cli}`;
  };

  const downloadProposal = async (id) => {
    try {
      const r = await api.get(`/proposals/${id}/download`, { responseType: "blob" });
      saveBlobResponse(r, _fname(id, "Proposta"));
    } catch { toast.error("Proposta ainda não gerada. Abra e clique em Gerar."); }
  };

  const shareProposal = async (p) => {
    if (p.superseded_by_id) return toast.error("Esta versão foi substituída. Compartilhe a revisão vigente.");
    setSharingId(p.id);
    try {
      const r = await api.post(`/proposals/${p.id}/share`);
      const url = r.data.public_url || `${window.location.origin}${r.data.public_path}`;
      setShareInfo({ proposal: p, url });
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link seguro copiado. PDF e QR Code foram sincronizados com esta versão.");
      } catch {
        toast.success("Link seguro gerado. Use as opções de compartilhamento.");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao gerar link público");
    } finally { setSharingId(null); }
  };

  const outreach = async (channel) => {
    if (!shareInfo?.proposal?.id || !shareInfo?.url) return;
    try {
      const r = await api.post(`/proposals/${shareInfo.proposal.id}/outreach`, { channel, public_url: shareInfo.url, send: false });
      if (r.data?.href) window.location.href = r.data.href;
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao preparar compartilhamento"); }
  };

  const copyShareLink = async () => {
    if (!shareInfo?.url) return;
    try { await navigator.clipboard.writeText(shareInfo.url); toast.success("Link copiado"); }
    catch { window.prompt("Copie o link:", shareInfo.url); }
  };

  const changeStatus = async (id, status, motivo = "") => {
    const p = items.find((x) => x.id === id);
    if (p?.superseded_by_id) {
      toast.error("Esta versão foi substituída e não pode mudar de etapa comercial.");
      return false;
    }
    const msg = { aceita: "Proposta marcada como aceita", recusada: "Perda registrada", enviada: "Proposta marcada como enviada" };
    try {
      await api.patch(`/proposals/${id}/status`, { status, motivo });
      toast.success(msg[status] || "Status atualizado");
      await load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao atualizar status");
      return false;
    }
  };

  const markRecusada = async (id) => {
    const motivo = window.prompt("Motivo da perda/recusa (recomendado para métricas comerciais):", "") ?? "";
    await changeStatus(id, "recusada", motivo);
  };

  const acceptAndContract = async (id) => {
    const ok = await changeStatus(id, "aceita");
    if (ok) await generateContract(id);
  };

  const createRevision = async (p) => {
    if (p.superseded_by_id) return toast.error("Use a versão vigente para criar uma nova revisão.");
    const suggested = p.revision_request_message || "";
    const reason = window.prompt("Motivo da revisão (opcional):", suggested);
    if (reason === null) return;
    try {
      const r = await api.post(`/proposals/${p.id}/revision`, { reason });
      toast.success(`Revisão ${r.data?.revision_label || ""} criada como ${r.data?.codigo || "rascunho"}`.trim());
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao criar revisão"); }
  };

  const openRevisionChain = async (p) => {
    setRevisionChain({ open: true, loading: true, proposal: p, items: [] });
    try {
      const r = await api.get(`/proposals/${p.id}/revisions`);
      setRevisionChain({ open: true, loading: false, proposal: p, items: r.data?.items || [] });
    } catch (e) {
      setRevisionChain((x) => ({ ...x, loading: false }));
      toast.error(e.response?.data?.detail || "Falha ao carregar cadeia de revisões");
    }
  };

  const generateContract = async (id) => {
    toast.loading("Gerando contrato...", { id: `contract-${id}` });
    try {
      const r = await api.post(`/proposals/${id}/contract`, null, { responseType: "blob" });
      const blob = r.data instanceof Blob ? r.data : new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      saveBlobResponse({ data: blob, headers: r.headers }, _fname(id, "Contrato"));
      toast.dismiss(`contract-${id}`);
      toast.success("Contrato gerado");
      await load();
    } catch (e) {
      toast.dismiss(`contract-${id}`);
      toast.error(e.response?.data?.detail || "Falha ao gerar contrato");
    }
  };

  const downloadContract = async (id) => {
    try {
      const r = await api.get(`/proposals/${id}/contract/download`, { responseType: "blob" });
      saveBlobResponse(r, _fname(id, "Contrato"));
    } catch { toast.error("Contrato ainda não foi gerado"); }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir esta proposta?")) return;
    try { await api.delete(`/proposals/${id}`); toast.success("Excluída"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha ao excluir"); }
  };

  const openTimeline = async (p) => {
    setTimeline({ open: true, loading: true, proposal: p, events: [] });
    try {
      const r = await api.get(`/proposals/${p.id}/timeline`);
      setTimeline({ open: true, loading: false, proposal: p, events: r.data?.events || [] });
    } catch (e) {
      setTimeline((t) => ({ ...t, loading: false }));
      toast.error(e.response?.data?.detail || "Falha ao carregar timeline");
    }
  };

  const openFollowUp = (p) => {
    if (p.superseded_by_id) return toast.error("Agende o contato na revisão vigente.");
    const initial = p.next_follow_up_at ? localInputValue(new Date(p.next_follow_up_at)) : localInputValue();
    setFollowUp({ open: true, proposal: p, scheduled_at: initial, channel: p.follow_up_channel || "telefone", note: p.follow_up_note || "" });
  };

  const saveFollowUp = async () => {
    if (!followUp.proposal?.id || !followUp.scheduled_at) return;
    try {
      await api.patch(`/proposals/${followUp.proposal.id}/follow-up`, {
        scheduled_at: new Date(followUp.scheduled_at).toISOString(),
        channel: followUp.channel,
        note: followUp.note,
      });
      toast.success("Próxima ação agendada");
      setFollowUp((f) => ({ ...f, open: false }));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao agendar follow-up"); }
  };

  const completeFollowUp = async (p) => {
    if (p.superseded_by_id) return toast.error("Registre o contato na revisão vigente.");
    const note = window.prompt("Resumo do contato realizado:", p.follow_up_note || "") ?? "";
    try {
      await api.post(`/proposals/${p.id}/follow-up/complete`, { channel: p.follow_up_channel || "telefone", note });
      toast.success("Follow-up concluído e registrado na timeline");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao concluir follow-up"); }
  };

  const commercialItems = (p) => {
    const hasContract = Boolean(p.contrato_path);
    if (p.superseded_by_id) {
      return (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Histórico da revisão</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => openTimeline(p)}><History className="size-4 mr-2" /> Timeline comercial</DropdownMenuItem>
          {isAdmin && <DropdownMenuItem onClick={() => openRevisionChain(p)}><GitBranch className="size-4 mr-2" /> Cadeia de revisões</DropdownMenuItem>}
        </>
      );
    }
    return (
      <>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>CRM / ciclo comercial</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => openTimeline(p)}><History className="size-4 mr-2" /> Timeline comercial</DropdownMenuItem>
        {isAdmin && <DropdownMenuItem onClick={() => openRevisionChain(p)}><GitBranch className="size-4 mr-2" /> Cadeia de revisões</DropdownMenuItem>}
        {(p.status === "rascunho" || p.status === "enviada") && <DropdownMenuItem onClick={() => openFollowUp(p)}><CalendarClock className="size-4 mr-2" /> {p.next_follow_up_at ? "Reagendar follow-up" : "Agendar follow-up"}</DropdownMenuItem>}
        {p.next_follow_up_at && <DropdownMenuItem onClick={() => completeFollowUp(p)}><CheckCircle2 className="size-4 mr-2" /> Concluir follow-up</DropdownMenuItem>}
        {(p.status === "rascunho" || p.status === "enviada") && (
          <DropdownMenuItem onClick={() => shareProposal(p)} disabled={sharingId === p.id}>
            <Link2 className="size-4 mr-2 text-primary" /> {sharingId === p.id ? "Sincronizando PDF/link…" : p.status === "enviada" ? "Rotacionar link seguro" : "Compartilhar proposta"}
          </DropdownMenuItem>
        )}
        {p.status === "rascunho" && <DropdownMenuItem onClick={() => changeStatus(p.id, "enviada")}><Send className="size-4 mr-2" /> Marcar enviada sem link</DropdownMenuItem>}
        {p.status === "enviada" && (
          <>
            <DropdownMenuItem onClick={() => changeStatus(p.id, "aceita")}><CheckCircle2 className="size-4 mr-2 text-emerald-500" /> Registrar aceite manual</DropdownMenuItem>
            <DropdownMenuItem onClick={() => acceptAndContract(p.id)}><FileCheck className="size-4 mr-2 text-emerald-500" /> Aceitar + gerar contrato</DropdownMenuItem>
            <DropdownMenuItem onClick={() => markRecusada(p.id)}><XCircle className="size-4 mr-2 text-destructive" /> Registrar perda</DropdownMenuItem>
          </>
        )}
        {p.status === "aceita" && (
          <>
            <DropdownMenuSeparator />
            {p.client_id && can("can_access_operations") && <Link to={`/clientes/${p.client_id}/360`}><DropdownMenuItem><ArrowRight className="size-4 mr-2" /> Organizar execução no cliente</DropdownMenuItem></Link>}
            <DropdownMenuLabel>Contrato</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => generateContract(p.id)}><FileText className="size-4 mr-2 text-primary" /> {hasContract ? "Regenerar contrato" : "Gerar contrato"}</DropdownMenuItem>
            {hasContract && <DropdownMenuItem onClick={() => downloadContract(p.id)}><Download className="size-4 mr-2" /> Baixar contrato</DropdownMenuItem>}
          </>
        )}
      </>
    );
  };

  const primaryAction = (p) => {
    const superseded = Boolean(p.superseded_by_id);
    if (superseded) return <Link to={`/propostas/${p.id}`}><Button size="sm" variant="outline">Visualizar</Button></Link>;
    if (p.needs_revision && isAdmin) return <Button size="sm" onClick={() => createRevision(p)}>Criar revisão</Button>;
    if (p.status === "rascunho") return <Link to={`/propostas/${p.id}`}><Button size="sm">Continuar</Button></Link>;
    if (p.status === "enviada" && !p.next_follow_up_at) return <Button size="sm" variant="outline" onClick={() => openFollowUp(p)}>Agendar contato</Button>;
    if (p.status === "enviada") return <Link to={`/propostas/${p.id}`}><Button size="sm" variant="outline">Abrir</Button></Link>;
    if (p.status === "aceita" && !p.contrato_path && canChangeStatus) return <Button size="sm" variant="outline" onClick={() => generateContract(p.id)}>Gerar contrato</Button>;
    if (p.status === "aceita" && p.contrato_path) return <Button size="sm" variant="outline" onClick={() => downloadContract(p.id)}>Contrato</Button>;
    return <Link to={`/propostas/${p.id}`}><Button size="sm" variant="outline">Visualizar</Button></Link>;
  };

  const counts = crm?.counts || {};
  const lossReasons = crm?.loss_reasons || [];
  const revisionPending = items.filter((p) => !p.superseded_by_id && p.status === "enviada" && p.needs_revision).length;
  const activeCount = items.filter((p) => !p.superseded_by_id).length;

  return (
    <>
      <Header
        title="Propostas"
        subtitle={`${activeCount} oportunidade(s) vigente(s). Crie, acompanhe e saiba qual é o próximo passo de cada negociação.`}
        actions={<Link to="/propostas/nova"><Button data-testid="new-proposal-btn"><Plus className="size-4 mr-1" /> Nova proposta</Button></Link>}
      />
      <div className="page-container space-y-5" data-testid="proposals-list-page">
        {!loading && crm && (
          <>
            <div>
              <h2 className="text-lg font-semibold">Visão rápida</h2>
              <p className="mt-1 text-sm text-muted-foreground">O que importa para o acompanhamento diário.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Kpi icon={FileText} label="Em negociação" value={counts.enviada || 0} sub={`${counts.rascunho || 0} rascunho(s) ainda não enviados`} />
              <Kpi icon={MessageSquareText} label="Ajustes solicitados" value={revisionPending} sub="Clientes aguardando uma nova revisão" alert={revisionPending > 0} />
              <Kpi icon={CalendarClock} label="Follow-ups vencidos" value={crm.followups_due || 0} sub={`${crm.followups_next_7d || 0} contato(s) nos próximos 7 dias`} alert={(crm.followups_due || 0) > 0} />
              <Kpi icon={CheckCircle2} label="Conversão" value={`${Number(crm.conversion_pct || 0).toFixed(0)}%`} sub={`${counts.aceita || 0} aceita(s) · ${counts.recusada || 0} perdida(s)`} />
            </div>

            {isAdmin && (
              <details className="rounded-xl border border-border/75 bg-card p-4">
                <summary className="cursor-pointer text-sm font-semibold">Ver indicadores financeiros e comerciais avançados</summary>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <Kpi icon={CircleDollarSign} label="Pipeline" value={formatBRL(crm.pipeline_value || 0)} sub="Valor total das oportunidades abertas" />
                  <Kpi icon={TrendingUp} label="Pipeline ponderado" value={formatBRL(crm.weighted_pipeline_value || 0)} sub="Probabilidade pelo estágio e visualização" />
                  <Kpi icon={BarChart3} label="Ticket médio" value={formatBRL(crm.avg_ticket || 0)} sub={`Fechamento médio: ${Number(crm.avg_close_days || 0).toFixed(1)} dias`} />
                  <Kpi icon={TrendingUp} label="Margem média" value={formatPct(crm.avg_margin_pct || 0)} sub={`Desconto médio: ${Number(crm.avg_discount_pct || 0).toFixed(1)}%`} />
                </div>
                {lossReasons.length > 0 && (
                  <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-medium text-muted-foreground">Motivos de perda:</span>
                    {lossReasons.slice(0, 5).map((x) => <span key={x.reason} className="rounded-full bg-muted px-2.5 py-1">{x.reason} · {x.count}</span>)}
                  </div>
                )}
              </details>
            )}
          </>
        )}

        {!loading && items.length > 0 && (
          <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-3 xl:flex-row xl:items-center">
            <div className="relative min-w-0 flex-1 xl:max-w-sm">
              <Search className="size-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-8" placeholder="Buscar código, cliente ou solicitação…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_FILTERS.map((f) => (
                <button key={f.key} onClick={() => setStatusFilter(f.key)} className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${statusFilter === f.key ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted/40"}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="size-3.5 mr-1" /> Atualizar</Button>
          </div>
        )}

        {loading && <div className="text-muted-foreground text-sm">Carregando propostas...</div>}
        {!loading && items.length === 0 && (
          <EmptyState testid="proposals-empty" title="Nenhuma proposta ainda" description="Crie a primeira proposta para iniciar o acompanhamento comercial." action={<Link to="/propostas/nova"><Button><Plus className="size-4 mr-1" /> Nova proposta</Button></Link>} />
        )}

        {!loading && items.length > 0 && (
          <div className="border border-border rounded-xl overflow-x-auto bg-card">
            <table className="w-full text-sm min-w-[1100px]">
              <caption className="text-left p-3"><label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={showDetails} onChange={(e) => setShowDetails(e.target.checked)} />Mostrar data, margem e engajamento</label></caption>
              <thead className="bg-muted/30">
                <tr>
                  <th className="text-left px-4 py-3 label-tech">Código</th>
                  <th className="text-left px-4 py-3 label-tech">Cliente</th>
                  {showDetails && <th className="text-left px-4 py-3 label-tech">Data</th>}
                  {isAdmin && <th className="text-right px-4 py-3 label-tech">Valor</th>}
                  {isAdmin && showDetails && <th className="text-right px-4 py-3 label-tech">Margem</th>}
                  {showDetails && <th className="text-left px-4 py-3 label-tech">Engajamento</th>}
                  <th className="text-left px-4 py-3 label-tech">Próximo contato</th>
                  <th className="text-center px-4 py-3 label-tech">Status</th>
                  <th className="text-right px-4 py-3 label-tech">Próxima ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.length === 0 && <tr><td colSpan={showDetails ? (isAdmin ? 9 : 7) : (isAdmin ? 6 : 5)} className="px-4 py-6 text-center text-muted-foreground">Nenhuma proposta encontrada.</td></tr>}
                {filtered.map((p) => {
                  const superseded = Boolean(p.superseded_by_id);
                  const st = superseded ? STATUS_LABEL.substituida : (STATUS_LABEL[p.status] || STATUS_LABEL.rascunho);
                  const cli = p.client_snapshot || {};
                  const margem = p.calc?.margem_pct ?? 0;
                  const hasContract = Boolean(p.contrato_path);
                  const followDate = p.next_follow_up_at ? new Date(p.next_follow_up_at) : null;
                  const followLate = followDate && followDate < new Date();
                  return (
                    <tr key={p.id} className={superseded ? "bg-muted/20 opacity-75 hover:opacity-100" : p.needs_revision ? "bg-amber-500/[0.035] hover:bg-amber-500/[0.07]" : "hover:bg-muted/20"}>
                      <td className="px-4 py-3 font-mono text-xs text-primary">
                        <button onClick={() => isAdmin && openRevisionChain(p)} className={isAdmin ? "hover:underline" : ""}>{displayProposalCode(p)}</button>
                        {p.revision_number === 0 && <div className="mt-0.5 text-[10px] text-muted-foreground">versão original</div>}
                      </td>
                      <td className="px-4 py-3 font-medium">{cli.razao_social || "-"}</td>
                      {showDetails && <td className="px-4 py-3 font-mono text-xs">{formatDateBR(p.data || p.created_at)}</td>}
                      {isAdmin && <td className="px-4 py-3 text-right font-mono">{formatBRL(p.calc?.valor_total || 0)}</td>}
                      {isAdmin && showDetails && <td className={`px-4 py-3 text-right font-mono ${margem < 20 ? "text-destructive" : "text-primary"}`}>{formatPct(margem)}</td>}
                      {showDetails && <td className="px-4 py-3 text-xs max-w-[250px]">
                        {superseded ? (
                          <button onClick={() => openTimeline(p)} className="text-left rounded-md border border-slate-500/20 bg-slate-500/5 px-2 py-1 text-muted-foreground hover:bg-muted/40">
                            <span className="flex items-center gap-1 font-medium"><History className="size-3.5" /> Substituída por {p.superseded_by_code || "revisão posterior"}</span>
                          </button>
                        ) : (
                          <>
                            {p.needs_revision && (
                              <button onClick={() => openTimeline(p)} className="mb-1.5 text-left rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-amber-700 dark:text-amber-300 hover:bg-amber-500/15" title={p.revision_request_message || "Cliente solicitou ajuste"}>
                                <span className="flex items-center gap-1 font-medium"><MessageSquareText className="size-3.5" /> Ajuste solicitado</span>
                                {p.revision_request_message && <span className="mt-0.5 block max-w-[220px] truncate opacity-80">{p.revision_request_message}</span>}
                              </button>
                            )}
                            {p.status === "enviada" ? (
                              <div className="space-y-0.5">
                                <div className="flex items-center gap-1.5"><Eye className="size-3.5 text-primary" /><span>{p.view_count || 0} visualização(ões)</span></div>
                                <div className="text-muted-foreground">Última: {dtBR(p.last_viewed_at)}</div>
                              </div>
                            ) : p.status === "aceita" ? <span className="text-emerald-600 dark:text-emerald-400">Fechada {p.data_aceite ? `· ${dtBR(p.data_aceite)}` : ""}</span> : <span className="text-muted-foreground">—</span>}
                          </>
                        )}
                      </td>}
                      <td className="px-4 py-3 text-xs">
                        {!superseded && p.next_follow_up_at ? (
                          <button onClick={() => openFollowUp(p)} className={`text-left hover:underline ${followLate ? "text-amber-600 dark:text-amber-400 font-medium" : "text-muted-foreground"}`}>
                            <div className="flex items-center gap-1.5"><Clock3 className="size-3.5" />{dtBR(p.next_follow_up_at)}</div>
                            {p.follow_up_channel && <div className="mt-0.5 capitalize">{p.follow_up_channel}</div>}
                          </button>
                        ) : !superseded && (p.status === "rascunho" || p.status === "enviada") ? <button onClick={() => openFollowUp(p)} className="text-muted-foreground hover:text-primary">+ agendar</button> : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        {!showDetails && p.needs_revision && <div className="text-xs font-medium text-amber-600 dark:text-amber-300">Ajuste solicitado</div>}
                        <span className={`px-2 py-0.5 text-xs font-mono uppercase rounded ${st.cls}`}>{st.label}</span>
                        {!superseded && p.needs_revision && <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400"><MessageSquareText className="w-3 h-3" /> ajuste</span>}
                        {!superseded && p.status === "aceita" && hasContract && <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400"><FileCheck className="w-3 h-3" /> contrato</span>}
                        {!superseded && p.vencida && <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-destructive"><AlertTriangle className="w-3 h-3" /> vencida</span>}
                        {!superseded && !p.vencida && p.dias_para_vencer != null && p.dias_para_vencer >= 0 && p.dias_para_vencer <= 7 && <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400"><AlertTriangle className="w-3 h-3" /> {p.dias_para_vencer}d</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {primaryAction(p)}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" aria-label={`Mais ações para ${displayProposalCode(p)}`}><MoreHorizontal className="size-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-64">
                              <DropdownMenuLabel>Mais ações</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => downloadProposal(p.id)}><Download className="size-4 mr-2" /> Baixar PDF</DropdownMenuItem>
                              <Link to={`/propostas/${p.id}`}><DropdownMenuItem><Pencil className="size-4 mr-2" /> {superseded || p.status === "aceita" ? "Visualizar" : isAdmin ? "Editar" : "Visualizar"}</DropdownMenuItem></Link>
                              {isAdmin && <DropdownMenuItem onClick={() => openRevisionChain(p)}><GitBranch className="size-4 mr-2" /> Cadeia de revisões</DropdownMenuItem>}
                              {isAdmin && !superseded && <DropdownMenuItem onClick={() => createRevision(p)}><Copy className="size-4 mr-2" /> {p.needs_revision ? "Criar revisão solicitada" : "Criar nova revisão"}</DropdownMenuItem>}
                              {canChangeStatus && commercialItems(p)}
                              {isAdmin && !superseded && !p.previous_revision_id && p.status !== "aceita" && <><DropdownMenuSeparator /><DropdownMenuItem onClick={() => del(p.id)} className="text-destructive"><Trash2 className="size-4 mr-2" /> Excluir</DropdownMenuItem></>}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!shareInfo} onOpenChange={(open) => !open && setShareInfo(null)}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Proposta pronta para compartilhar</DialogTitle>
            <DialogDescription>O PDF foi regenerado com o QR Code deste mesmo link seguro. Gerar outro link invalida o anterior.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-muted/20 p-3 text-xs font-mono break-all">{shareInfo?.url}</div>
            <div className="grid sm:grid-cols-3 gap-2">
              <Button variant="outline" onClick={copyShareLink}><Link2 className="size-4 mr-2" /> Copiar link</Button>
              <Button variant="outline" onClick={() => outreach("whatsapp")}><MessageCircle className="size-4 mr-2" /> WhatsApp</Button>
              <Button variant="outline" onClick={() => outreach("email")}><Mail className="size-4 mr-2" /> E-mail</Button>
            </div>
          </div>
          <DialogFooter><Button onClick={() => setShareInfo(null)}>Concluir</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={timeline.open} onOpenChange={(open) => setTimeline((t) => ({ ...t, open }))}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Timeline comercial · {displayProposalCode(timeline.proposal)}</DialogTitle>
            <DialogDescription>{timeline.proposal?.client_snapshot?.razao_social || "Histórico comercial e de engajamento"}</DialogDescription>
          </DialogHeader>
          {timeline.loading ? <div className="py-8 text-center text-sm text-muted-foreground">Carregando histórico...</div> : <Timeline events={timeline.events} />}
        </DialogContent>
      </Dialog>

      <Dialog open={revisionChain.open} onOpenChange={(open) => setRevisionChain((x) => ({ ...x, open }))}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Cadeia de revisões · {revisionChain.proposal?.codigo_base || revisionChain.proposal?.codigo || "proposta"}</DialogTitle>
            <DialogDescription>Cada revisão preserva a versão anterior e mantém somente a última versão disponível para negociação.</DialogDescription>
          </DialogHeader>
          {revisionChain.loading ? <div className="py-10 text-center text-sm text-muted-foreground">Carregando versões...</div> : <RevisionChain items={revisionChain.items} />}
        </DialogContent>
      </Dialog>

      <Dialog open={followUp.open} onOpenChange={(open) => setFollowUp((f) => ({ ...f, open }))}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Próxima ação comercial</DialogTitle>
            <DialogDescription>{followUp.proposal?.client_snapshot?.razao_social || "Agende o próximo contato desta oportunidade."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Data e hora</Label><Input type="datetime-local" className="mt-1" value={followUp.scheduled_at} onChange={(e) => setFollowUp((f) => ({ ...f, scheduled_at: e.target.value }))} /></div>
            <div>
              <Label>Canal</Label>
              <select className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={followUp.channel} onChange={(e) => setFollowUp((f) => ({ ...f, channel: e.target.value }))}>
                <option value="telefone">Telefone</option><option value="whatsapp">WhatsApp</option><option value="email">E-mail</option><option value="reuniao">Reunião</option><option value="outro">Outro</option>
              </select>
            </div>
            <div><Label>Objetivo / observação</Label><Textarea rows={3} className="mt-1" value={followUp.note} onChange={(e) => setFollowUp((f) => ({ ...f, note: e.target.value }))} placeholder="Ex.: confirmar análise do financeiro e agendar retorno" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFollowUp((f) => ({ ...f, open: false }))}>Cancelar</Button>
            <Button onClick={saveFollowUp}><CalendarClock className="size-4 mr-2" /> Salvar próxima ação</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

